import { datadogRum } from '@datadog/browser-rum'

/**
 * Counting the Genesys chat recovery flow (ASH-887) in Datadog RUM.
 *
 * Action names live here, one place per app, so a typo in one copy can't
 * silently split the cross-app query. Mirrors
 * `apps/member-app/lib/chat-recovery-telemetry.ts` — the two apps share no
 * module (legacy resolves `utils/*`, the shell is standalone Next.js), so this
 * follows the same mirrored-helper precedent as `utils/heap/withAshSession.js`
 * / `lib/ash-session.ts`.
 */

const SOURCE = 'genesys-chat-recovery'

const ACTION_NAMES = {
  attempt: 'member_care_chat_recovery_attempt',
  outcome: 'member_care_chat_recovery_outcome',
  suppressed: 'member_care_chat_open_suppressed',
}

/**
 * Absent, never empty — matches the "absent, never empty" rule in
 * `withAshSession` so a blank sessionId can't be mistaken for a real-but-blank
 * one. Queryable as `-@context.sessionId:*`.
 *
 * @param {object} context
 * @param {string} [sessionId]
 * @returns {object}
 */
const withSessionId = (context, sessionId) =>
  sessionId ? { ...context, sessionId } : context

/**
 * @param {object} params
 * @param {'initial' | 'bare-retry' | 'clear-retry'} params.stage
 * @param {number} params.attempt
 * @param {string} [params.sessionId]
 */
export const trackRecoveryAttempt = ({ stage, attempt, sessionId }) => {
  datadogRum.addAction(
    ACTION_NAMES.attempt,
    withSessionId({ source: SOURCE, stage, attempt }, sessionId),
  )
}

/**
 * @param {object} params
 * @param {'recovered' | 'failed'} params.outcome
 * @param {'initial' | 'bare-retry' | 'clear-retry'} params.stage
 * @param {'timeout' | 'error' | 'genesys-unavailable'} [params.reason]
 * @param {number} params.openAttempts
 * @param {string} [params.sessionId]
 */
export const trackRecoveryOutcome = ({
  outcome,
  stage,
  reason,
  openAttempts,
  sessionId,
}) => {
  datadogRum.addAction(
    ACTION_NAMES.outcome,
    withSessionId(
      { source: SOURCE, outcome, stage, reason, openAttempts },
      sessionId,
    ),
  )
}

/**
 * @param {object} [params]
 * @param {string} [params.sessionId]
 */
export const trackOpenSuppressed = ({ sessionId } = {}) => {
  datadogRum.addAction(
    ACTION_NAMES.suppressed,
    withSessionId({ source: SOURCE }, sessionId),
  )
}
